// Admin > Company Details: switch marketing messages (Admin > Marketing invitations and vendor
// login emails) on or off. Takes effect at once; while off everything is recorded as "held".
import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import {
  Paper, Stack, Typography, Switch, Chip, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox,
  FormControlLabel, Link, Box,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import { MARKETING_SETTINGS_QUERY, SET_MARKETING_SENDING_MUTATION, MESSAGING_SETUP_QUERY } from '../graphql/queries';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

export default function MarketingSendingCard() {
  const { data, refetch } = useQuery(MARKETING_SETTINGS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: setupData } = useQuery(MESSAGING_SETUP_QUERY, { fetchPolicy: 'cache-and-network' });
  const [save, { loading, error }] = useMutation(SET_MARKETING_SENDING_MUTATION, { onCompleted: () => refetch() });
  const [confirming, setConfirming] = useState(false);
  const [agree, setAgree] = useState(false);
  const m = data?.marketingSettings;
  const c = setupData?.messagingSetup?.connections;
  if (!m) return null;

  const channels = c ? [
    ['Email', c.email.configured, c.email.configured ? 'SMTP set up' : 'not set up: emails will fail'],
    ['WhatsApp', c.whatsapp.configured, c.whatsapp.configured ? 'connected' : 'not connected: sent by hand from WhatsApp'],
    ['SMS', c.sms.isActive && c.sms.configured, c.sms.isActive && c.sms.configured ? 'gateway on' : 'no gateway: SMS will fail'],
  ] : [];
  const turnOn = async () => { await save({ variables: { enabled: true } }).catch(() => {}); setConfirming(false); setAgree(false); };

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, p: 3, mt: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <CampaignIcon color="primary" />
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>Marketing messages</Typography>
        <Chip size="small" color={m.sendingEnabled ? 'success' : 'default'} label={m.sendingEnabled ? 'On: messages are sent' : 'Off: messages are held'} />
        <Switch checked={m.sendingEnabled} disabled={loading || m.sendingLocked}
          onChange={e => (e.target.checked ? setConfirming(true) : save({ variables: { enabled: false } }).catch(() => {}))}
          inputProps={{ 'aria-label': 'Marketing messages' }} />
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        Covers the invitations sent from <Link component={RouterLink} to="/dashboard/admin/marketing">Admin → Marketing</Link> (email, SMS, WhatsApp) and the login
        details emailed to vendors who accept. While off, nothing leaves the platform: each message is recorded against the lead as "held".
        Takes effect immediately.
      </Typography>
      {m.sendingUpdatedAt && (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Last switched {m.sendingEnabled ? 'on' : 'off'} {fmt(m.sendingUpdatedAt)}{m.sendingUpdatedBy ? ` by ${m.sendingUpdatedBy}` : ''}
        </Typography>
      )}
      {m.sendingLocked && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          The server locks marketing messages off (MARKETING_SEND_ENABLED=false in the backend settings). Remove that line and restart the backend to use this switch.
        </Alert>
      )}
      {m.appUrlIsLocal && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          The app's public address is <b>{m.appUrl}</b>, so invitation and sign-in links would only work on this computer. Set the
          {' '}<b>App public address</b> in Company Details above (Edit Details) to the address people use, e.g. your domain, before sending.
        </Alert>
      )}
      {channels.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {channels.map(([k, ok, l]) => <Chip key={k} size="small" variant="outlined" color={ok ? 'success' : 'default'} label={`${k}: ${l}`} />)}
          <Button size="small" component={RouterLink} to="/dashboard/admin/message-templates">Connections & templates</Button>
        </Stack>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error.message}</Alert>}

      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Switch marketing messages on?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={1.5}>
            From now on, <b>Send invitation</b> in Admin → Marketing sends real emails, SMS and WhatsApp messages to the selected leads, and vendors who accept
            are emailed their login details.
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
            {channels.filter(([, ok]) => !ok).map(([k, , l]) => <Typography component="li" variant="body2" key={k}>{k}: {l}</Typography>)}
            {m.appUrlIsLocal && <Typography component="li" variant="body2" color="error">Invitation links point at {m.appUrl} (this computer only)</Typography>}
            <Typography component="li" variant="body2">Promotional SMS need DLT registration and reach only numbers not on the DND list (TRAI).</Typography>
          </Box>
          <FormControlLabel sx={{ mt: 1.5 }} control={<Checkbox checked={agree} onChange={e => setAgree(e.target.checked)} />}
            label={<Typography variant="body2">I confirm these businesses may be contacted, and that each message offers "Do not contact me".</Typography>} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirming(false); setAgree(false); }}>Cancel</Button>
          <Button variant="contained" disabled={!agree || loading} onClick={turnOn}>Switch on</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
